// @TODO - once my PR #765 is merged, use the shared type definition for ProductSize
type ProductSize = {
  width: number | string
  height: number | string
  widthIn: number | string
  heightIn: number | string
}

/**
 * Calculate product PPI (pixels per inch) from product or page size definitions.
 *
 * This determines the mapping from product-canvas pixels to printed inches by finding
 * the minimum PPI between width and height dimensions. The minimum is used to ensure
 * accurate calculations for both square and non-square aspect ratios.
 *
 * @param productSize - Primary product size definition
 * @param pageSize - Fallback page size definition
 * @returns Product PPI value, or null if invalid/missing dimensions
 *
 * @example
 * ```typescript
 * // Product canvas: 840×696 pixels for 8.75×7.25 inches
 * const ppi = calculateProductPPI(
 *   { width: 840, height: 696, widthIn: 8.75, heightIn: 7.25 },
 *   null
 * )
 * // Returns: 96 PPI (min of 96 and 96)
 * ```
 */
export const calculateProductPPI = (
  productSize: ProductSize | undefined,
  pageSize: ProductSize | undefined,
): number | null => {
  const src = productSize || pageSize

  if (!src) return null

  const coerce = (dimension: number | string | undefined): number => {
    if (dimension === undefined || dimension === null) return NaN
    if (typeof dimension === 'number') return dimension
    const parsed = parseFloat(String(dimension))
    return Number.isNaN(parsed) ? NaN : parsed
  }

  const sourceWidthPixels = coerce(src.width)
  const sourceHeightPixels = coerce(src.height)
  const sourceWidthIn = coerce(src.widthIn)
  const sourceHeightIn = coerce(src.heightIn)

  const isValid =
    Number.isFinite(sourceWidthPixels) &&
    Number.isFinite(sourceHeightPixels) &&
    Number.isFinite(sourceWidthIn) &&
    Number.isFinite(sourceHeightIn) &&
    sourceWidthPixels > 0 &&
    sourceHeightPixels > 0 &&
    sourceWidthIn > 0 &&
    sourceHeightIn > 0

  if (!isValid) return null

  // Calculate PPI for both dimensions and use the minimum
  // This ensures accuracy for products with non-square aspect ratios
  const ppiX = sourceWidthPixels / sourceWidthIn
  const ppiY = sourceHeightPixels / sourceHeightIn
  const derivedPixelsPerInch = Math.min(ppiX, ppiY)

  const isValidDerivedPixelsPerInch =
    isFinite(derivedPixelsPerInch) && derivedPixelsPerInch > 0

  return isValidDerivedPixelsPerInch ? derivedPixelsPerInch : null
}
